import type { ReactNode } from 'react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'
import type { AnalysisMode } from '@/components/v2/tools/amazon-listing/CampaignCreator/AnalysisModeCards'
import { TitleAnalysis } from './TitleAnalysis'
import { BulletsAnalysis } from './BulletsAnalysis'
import { DescriptionAnalysis } from './DescriptionAnalysis'
import { ImageAuditOverview } from './ImageAuditOverview'
import { ImageConceptOverview } from './ImageConceptOverview'
import { KeywordMatrix } from './KeywordMatrix'
import { PricingAnalysis } from './PricingAnalysis'
import { ReviewInsights } from './ReviewInsights'
import { ConversionDriverTable } from './ConversionDriverTable'
import { ConversionBlockers } from './ConversionBlockers'
import { StrengthsWeaknessTable } from './StrengthsWeaknessTable'
import { ProductImprovements } from './ProductImprovements'
import { ComplianceChecklist } from './ComplianceChecklist'
import { CompetitiveBenchmark } from './CompetitiveBenchmark'
import { Positioning } from './Positioning'
import { ChannelReadiness } from './ChannelReadiness'
import { AttributeIntelligence } from './AttributeIntelligence'
import { BrandAnalysis } from './BrandAnalysis'
import { ProductSummary } from './ProductSummary'
import { SMALL_AUDIT_LOCKED_SECTIONS } from './auditConstants'

export interface SectionDef {
  id: string
  labelKey: string
  content: unknown
  node: ReactNode
}

/** Sections shown in the compact report view. */
const COMPACT_SECTION_IDS = new Set([
  'product-summary',
  'title-analysis',
  'strengths-weaknesses',
  'conversion-blockers',
  'product-improvements',
])

interface VisibleSectionsOptions {
  mode?: 'compact' | 'full'
  analysisMode?: AnalysisMode
}

/**
 * Single source of truth for which report sections are visible (and in which order).
 * Used by both the section stack/accordion and the scroll-spy sidebar so they stay
 * perfectly in sync — also in compact mode.
 */
export function getVisibleSections(
  report: ListingAuditReport,
  { mode = 'full', analysisMode }: VisibleSectionsOptions = {},
): SectionDef[] {
  const isCompact = mode === 'compact'
  const isChannel = report.audit_mode === 'channel_product'
  const isSmall = analysisMode === 'small'
  const lockedIds = new Set(SMALL_AUDIT_LOCKED_SECTIONS.map((s) => s.id))

  const sections: SectionDef[] = [
    { id: 'product-summary', labelKey: 'amazonAudit.sections.productSummary', content: report.product_summary, node: <ProductSummary data={report.product_summary!} /> },
    { id: 'title-analysis', labelKey: 'amazonAudit.sections.titleAnalysis', content: report.title_analysis, node: <TitleAnalysis analysis={report.title_analysis!} /> },
    { id: 'brand-analysis', labelKey: 'amazonAudit.sections.brandAnalysis', content: report.brand_analysis, node: <BrandAnalysis data={report.brand_analysis!} /> },
    { id: 'bullets-analysis', labelKey: 'amazonAudit.sections.bulletsAnalysis', content: report.bullets_analysis, node: <BulletsAnalysis analysis={report.bullets_analysis!} /> },
    { id: 'description-analysis', labelKey: 'amazonAudit.sections.descriptionAnalysis', content: report.description_analysis, node: <DescriptionAnalysis analysis={report.description_analysis!} /> },
    { id: 'image-audit', labelKey: 'amazonAudit.sections.imageAudit', content: report.image_audit, node: <ImageAuditOverview imageAudit={report.image_audit!} /> },
    ...(report.image_concept ? [{ id: 'image-concept', labelKey: 'amazonAudit.sections.imageConcept', content: report.image_concept, node: <ImageConceptOverview concept={report.image_concept} /> }] : []),
    { id: 'keyword-matrix', labelKey: 'amazonAudit.sections.keywordMatrix', content: report.keyword_matrix, node: <KeywordMatrix matrix={report.keyword_matrix!} /> },
    { id: 'pricing-analysis', labelKey: 'amazonAudit.sections.pricingAnalysis', content: report.pricing_analysis, node: <PricingAnalysis analysis={report.pricing_analysis!} /> },
    { id: 'review-insights', labelKey: 'amazonAudit.sections.reviewInsights', content: report.review_insights, node: <ReviewInsights insights={report.review_insights!} /> },
    { id: 'conversion-drivers', labelKey: 'amazonAudit.sections.conversionDrivers', content: report.conversion_drivers, node: <ConversionDriverTable drivers={report.conversion_drivers!} /> },
    { id: 'conversion-blockers', labelKey: 'amazonAudit.sections.conversionBlockers', content: report.conversion_blockers, node: <ConversionBlockers blockers={report.conversion_blockers ?? []} /> },
    { id: 'strengths-weaknesses', labelKey: 'amazonAudit.sections.strengthsWeaknesses', content: report.strengths ?? report.weaknesses, node: <StrengthsWeaknessTable strengths={report.strengths ?? []} weaknesses={report.weaknesses ?? []} /> },
    { id: 'product-improvements', labelKey: 'amazonAudit.sections.productImprovements', content: report.product_improvements, node: <ProductImprovements improvements={report.product_improvements!} /> },
    ...(report.audit_mode !== 'new_product' ? [{ id: 'compliance', labelKey: 'amazonAudit.sections.compliance', content: report.compliance, node: <ComplianceChecklist compliance={report.compliance!} /> }] : []),
    { id: 'attribute-intelligence', labelKey: 'amazonAudit.sections.attributeIntelligence', content: report.attribute_intelligence, node: <AttributeIntelligence data={report.attribute_intelligence!} /> },
    { id: 'competitive-benchmark', labelKey: 'amazonAudit.sections.competitiveBenchmark', content: report.competitive_benchmark, node: <CompetitiveBenchmark benchmark={report.competitive_benchmark!} tld={report.tld} /> },
    { id: 'positioning', labelKey: 'amazonAudit.sections.positioning', content: report.positioning, node: <Positioning data={report.positioning!} /> },
    ...(isChannel ? [{ id: 'channel-readiness', labelKey: 'amazonAudit.sections.channelReadiness', content: report.channel_readiness, node: <ChannelReadiness data={report.channel_readiness!} /> }] : []),
  ].filter((s) => {
    if (!s.content) return false
    if (isSmall && lockedIds.has(s.id)) return false
    if (isCompact && !COMPACT_SECTION_IDS.has(s.id)) return false
    return true
  })

  return sections
}
